import React, { useEffect, useRef } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { Checkbox } from './Checkbox';
import { ChampEtiquete } from './ChampEtiquete';
import { EnteteSection } from './EnteteSection';
import { MonthYearField } from './MonthYearField';
import { X } from './icons';
import { formatMonthYear } from '../utils/monthYear';
import { colors, formes, shadow, spacing, typography } from '../theme/tokens';
import type { EducationEntry, MonthYear } from '../types';
import { ongoingLabel } from '../utils/documentLabels';
import type { OutputLanguage } from '../types';
import { t } from '../i18n';

function emptyEntry(): EducationEntry {
  return { degree: '', school: '', date: { month: '', year: '' } as MonthYear, current: false };
}

function dateLabel(date: MonthYear, current: boolean, ongoing: string): string {
  const d = formatMonthYear(date);
  if (current) return d ? `${d} – ${ongoing}` : ongoing;
  return d;
}

export function serializeEducation(
  entries: EducationEntry[],
  // Langue du DOCUMENT : « En cours » est une valeur écrite dans le CV, pas un libellé.
  outputLanguage: OutputLanguage = 'fr',
): string {
  const ongoing = ongoingLabel(outputLanguage);
  return entries
    .map((e) => {
      const header = [e.degree.trim(), e.school.trim()].filter(Boolean).join(' - ');
      if (!header) return '';
      const label = dateLabel(e.date, e.current, ongoing);
      return label ? `${header} (${label})` : header;
    })
    .filter(Boolean)
    .join('\n');
}

interface EducationEditorProps {
  value: EducationEntry[];
  onChange: (entries: EducationEntry[]) => void;
  testID?: string;
}

export function EducationEditor({ value, onChange, testID }: EducationEditorProps) {
  const update = (index: number, patch: Partial<EducationEntry>) =>
    onChange(value.map((e, i) => (i === index ? { ...e, ...patch } : e)));
  const remove = (index: number) => onChange(value.filter((_, i) => i !== index));
  // Rang de la ligne qui vient d'être ajoutée : son premier champ prend le focus à l'apparition,
  // sans quoi une ligne créée sous le bas de l'écran passerait inaperçue.
  const nouvelle = useRef(-1);
  // Une seule fois : sans cela, une ligne remontée plus tard à ce rang reprendrait le focus.
  useEffect(() => {
    nouvelle.current = -1;
  });

  return (
    <View testID={testID} style={styles.container}>
      <EnteteSection
        titre={t('editors.education.titre')}
        ajouterA11y={t('editors.education.add')}
        ajouterTestID="edu-add"
        onAjouter={() => {
          nouvelle.current = value.length;
          onChange([...value, emptyEntry()]);
        }}
      />

      {value.map((entry, index) => (
        <View key={index} testID={`edu-row-${index}`} style={styles.row}>
          <View style={styles.rowHeader}>
            <Text style={styles.rowTitle}>{t('editors.education.rowTitle', { n: index + 1 })}</Text>
            <Pressable
              testID={`edu-remove-${index}`}
              accessibilityRole="button"
              accessibilityLabel={t('a11y.removeEducation')}
              onPress={() => remove(index)}
              style={styles.remove}
            >
              <X size={18} color={colors.grey500} />
            </Pressable>
          </View>

          <ChampEtiquete
            testID={`edu-degree-${index}`}
            autoFocus={index === nouvelle.current}
            label={t('editors.education.degree')}
            value={entry.degree}
            onChangeText={(degree) => update(index, { degree })}
          />
          <ChampEtiquete
            testID={`edu-school-${index}`}
            label={t('editors.education.school')}
            value={entry.school}
            onChangeText={(school) => update(index, { school })}
          />

          <Text style={styles.miniLabel}>{t('editors.education.date')}</Text>
          <MonthYearField
            value={entry.date}
            onChange={(date) => update(index, { date })}
            testID={`edu-date-${index}`}
          />
          <Checkbox
            label={t('editors.education.current')}
            checked={entry.current}
            onToggle={(current) => update(index, { current })}
            testID={`edu-current-${index}`}
          />
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { gap: spacing.sm },
  row: {
    gap: spacing.sm,
    backgroundColor: colors.white,
    borderRadius: formes.rayon.md,
    padding: spacing.md,
    ...shadow.card,
  },
  rowHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  rowTitle: { fontSize: typography.bodyMd.size, fontWeight: '800', color: colors.night },
  remove: { minWidth: formes.cible, minHeight: formes.cible, alignItems: 'flex-end', justifyContent: 'center' },
  miniLabel: {
    fontSize: typography.caption.size,
    fontWeight: '700',
    color: colors.grey700,
  },
});
